import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Newspaper, Plus, Search, Edit3, Trash2, EyeOff, Eye, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Toaster, toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

export default function AdminBlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/blog/posts`);
      setPosts(data || []);
    } catch {
      toast.error("Failed to load blog posts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return posts;
    return posts.filter(
      (p) =>
        p.slug.toLowerCase().includes(q) ||
        p.title.toLowerCase().includes(q) ||
        (p.category || "").toLowerCase().includes(q)
    );
  }, [posts, query]);

  const handleDelete = async (slug) => {
    if (!window.confirm(`Delete post "${slug}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/blog/posts/${slug}`);
      setPosts((prev) => prev.filter((p) => p.slug !== slug));
      toast.success("Post deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const handleTogglePublish = async (post) => {
    try {
      const { data } = await axios.patch(`${API}/admin/blog/posts/${post.slug}`, {
        published: !post.published,
      });
      setPosts((prev) => prev.map((p) => (p.slug === post.slug ? { ...p, ...data } : p)));
      toast.success(data.published ? "Published." : "Unpublished.");
    } catch {
      toast.error("Update failed.");
    }
  };

  return (
    <div data-testid="admin-blog-page" className="bg-white min-h-full">
      <Toaster position="top-right" richColors />

      <div className="px-5 lg:px-8 py-5 border-b border-slate-200 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-slate-900">
            <Newspaper className="h-4 w-4 text-sky-600" />
            <h1 className="text-base font-bold">Blog Posts</h1>
            <span className="text-xs text-slate-400">({posts.length})</span>
          </div>
          <p className="text-xs text-slate-500 mt-1">Create, edit, and publish articles for /blog.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search posts…"
              className="pl-9 h-9 w-60 text-xs"
              data-testid="admin-blog-search"
            />
          </div>
          <Button
            onClick={() => navigate("/admin/blog/new")}
            data-testid="admin-blog-new"
            className="bg-sky-600 hover:bg-sky-700 text-white h-9 rounded-lg text-xs font-semibold"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> New post
          </Button>
        </div>
      </div>

      <div className="p-5 lg:p-8">
        {loading ? (
          <p className="text-sm text-slate-400 text-center py-10">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-slate-200 rounded-xl bg-slate-50/50">
            <p className="text-sm text-slate-500">
              {posts.length === 0 ? "No posts yet. Create your first post." : "No posts match your search."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto border border-slate-200 rounded-xl">
            <table className="w-full text-sm" data-testid="admin-blog-table">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 tracking-widest">
                <tr>
                  <th className="text-left px-4 py-2.5 font-semibold">Title</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Category</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Tags</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Date</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {filtered.map((p) => (
                  <tr key={p.slug} data-testid={`admin-blog-row-${p.slug}`} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-slate-900 line-clamp-1">{p.title}</p>
                      <p className="text-[10px] text-slate-400 font-mono mt-0.5">/{p.slug}</p>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">{p.category}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(p.tags || []).slice(0, 3).map((t) => (
                          <span
                            key={t}
                            className="text-[9px] font-medium text-sky-700 bg-sky-50 border border-sky-100 rounded-full px-1.5 py-0.5"
                          >
                            {t}
                          </span>
                        ))}
                        {(p.tags || []).length > 3 && (
                          <span className="text-[9px] text-slate-400">+{p.tags.length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">{p.date || fmtDate(p.date_ts)}</td>
                    <td className="px-4 py-3">
                      {p.published ? (
                        <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                          <Eye className="h-2.5 w-2.5" /> Published
                        </span>
                      ) : (
                        <span className="text-[10px] font-semibold text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                          <EyeOff className="h-2.5 w-2.5" /> Draft
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Link
                          to={`/blog/${p.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="View on site"
                          className="p-1.5 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Link>
                        <button
                          type="button"
                          onClick={() => handleTogglePublish(p)}
                          title={p.published ? "Unpublish" : "Publish"}
                          data-testid={`admin-blog-toggle-${p.slug}`}
                          className="p-1.5 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        >
                          {p.published ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        </button>
                        <Link
                          to={`/admin/blog/${p.slug}`}
                          data-testid={`admin-blog-edit-${p.slug}`}
                          className="p-1.5 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        >
                          <Edit3 className="h-3.5 w-3.5" />
                        </Link>
                        <button
                          type="button"
                          onClick={() => handleDelete(p.slug)}
                          title="Delete"
                          data-testid={`admin-blog-delete-${p.slug}`}
                          className="p-1.5 rounded text-slate-400 hover:bg-red-50 hover:text-red-500"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
